import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import styled, { css } from 'styled-components';
import { isBlank } from 'underscore.string';
import { useDebouncedCallback } from 'use-debounce';

import usePrevious from 'shared/hooks/use-previous';

export default function Search(props) {
  const { debounced = true, debounceTime = 250, initialValue, onUpdate, placeholder = 'Search...' } = props;
  const [value, setValue] = useState(initialValue);
  const previousInitialValue = usePrevious(initialValue);

  useEffect(() => {
    if (initialValue !== previousInitialValue) {
      setValue(initialValue);
    }
  }, [initialValue, previousInitialValue]);

  function callback(newValue) {
    onUpdate({ searchStr: newValue });
  }

  const [debouncedUpdate] = useDebouncedCallback(callback, debounceTime);

  function handleChange(event) {
    const newValue = event.target.value;
    setValue(newValue);

    if (debounced) {
      debouncedUpdate(newValue);
    } else {
      onUpdate({ searchStr: newValue });
    }
  }

  function handleClear() {
    setValue('');
    callback('');
  }

  function handleKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      callback(value);
    }
  }

  return (
    <Container {..._.pick(props, ['width', 'noBorder'])}>
      <Input
        data-testid='search-input'
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        type='text'
        value={value}
      />

      <SearchIcon>
        <img src='/icons/search-icon.svg' width='16px' />
      </SearchIcon>

      {!isBlank(value) && (
        <ClearIcon onClick={handleClear}>
          <img src='/icons/circle-x.svg' width='16px' />
        </ClearIcon>
      )}
    </Container>
  );
}

const Container = styled.div`
  background-color: #ffffff;
  border-radius: 20px;
  border: ${({ noBorder }) => (noBorder ? 'none' : '1px solid #acb8c0')};
  height: 33px;
  overflow: hidden;
  padding-left: 20px;
  position: relative;
  text-align: left;
  width: ${({ width }) => width || '250px'};
`;

const Input = styled.input`
  appearance: none;
  background-color: #fcfdfe;
  background: none;
  border-radius: 2px;
  border: none;
  box-shadow: none;
  box-sizing: border-box;
  color: #707478;
  font-size: 14px;
  height: 33px;
  padding: 13px 27px 13px 15px;
  width: 100%;

  :active,
  :focus,
  :hover {
    outline: none;
  }

  ::placeholder {
    color: #aeafaf;
  }
`;

const IconBase = css`
  color: #dbdbdb;
  margin-top: -7px;
  position: absolute;
  top: 50%;
  z-index: 4;

  svg {
    height: 16px;
    width: 16px;
  }
`;

const SearchIcon = styled.div`
  ${IconBase}
  left: 10px;
  pointer-events: none;
`;

const ClearIcon = styled.div`
  ${IconBase}
  cursor: pointer;
  right: 10px;
`;
